import { useEffect, useState } from "react";
import { Check, Layers, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/game/Mascot";
import { Leaderboard } from "@/components/game/Leaderboard";
import { ClassScore } from "@/components/game/ClassScore";
import { QuizStudent } from "@/modes/quiz/QuizStudent";
import { QuizTeacher } from "@/modes/quiz/QuizTeacher";
import { Final } from "@/modes/quiz/QuizBoard";
import { sfx } from "@/lib/sfx";

const ZONE_COLORS = ["#3B82F6", "#EF4444", "#22C55E", "#A855F7"];
const SHUFFLE_MS = 1800;
const teamOf = (state, id) => state.teams.find((t) => t.id === id);

const LastMove = ({ state }) => {
  const l = state.last;
  if (!l) return null;
  const team = teamOf(state, l.team);
  return (
    <div key={l.n} className={`flex items-center gap-3 rounded-full px-[1.2vw] py-[0.8vh] border-2 anim-pop ${l.ok ? "border-[#22C55E] bg-[#22C55E]/15" : "border-red-500 bg-red-500/10 anim-shake"}`} data-testid="boardsort-last">
      {l.ok ? <Check className="h-[3vh] w-[3vh] text-[#22C55E]" /> : <X className="h-[3vh] w-[3vh] text-red-400" />}
      <span className="text-[2.1vh] font-semibold">{!state.settings.closed_session && <><b style={{ color: team?.color || "#FACC15" }}>{l.name}</b>: </>}“{l.item}” → {l.target} {l.ok ? `doğru! +${l.points}` : "yanlış, kart desteye döndü"}</span>
    </div>
  );
};

const Zone = ({ t, i, state, onDrop, armed }) => {
  const placed = state.items.filter((it) => it.placed?.target === t.id);
  const c = ZONE_COLORS[i % ZONE_COLORS.length];
  return (
    <button type="button" onClick={() => onDrop(t.id)} disabled={!armed}
      className={`min-h-0 flex flex-col rounded-[1.75rem] border-[3px] text-left transition-transform duration-150 ${armed ? "border-dashed hover:scale-[1.01] cursor-pointer" : "cursor-default"}`}
      style={{ borderColor: armed ? c : `${c}88`, background: `linear-gradient(180deg, ${c}26, ${c}08)` }} data-testid={`boardsort-zone-${t.id}`}>
      <p className="font-display text-[min(2.6rem,4vh)] font-bold text-center py-[1vh]" style={{ color: c }}>{t.text}</p>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-wrap content-start gap-[0.8vh] p-[1.2vh]">
        {placed.map((it) => (
          <span key={it.id} className="rounded-2xl bg-[#0B0F16]/70 border-2 px-[0.9vw] py-[0.6vh] text-[min(1.5rem,2.2vh)] font-semibold anim-pop"
            style={{ borderColor: teamOf(state, it.placed.team)?.color || "#FACC15" }}>{it.text}</span>
        ))}
      </div>
    </button>
  );
};

const useShuffle = (card, roster, sound) => {
  const [tick, setTick] = useState(0);
  const [shuffling, setShuffling] = useState(false);
  const n = card?.n;
  useEffect(() => {
    if (!n) return undefined;
    setShuffling(true);
    if (sound) sfx.draw();
    const iv = setInterval(() => setTick((x) => x + 1), 90);
    const t = setTimeout(() => { clearInterval(iv); setShuffling(false); }, SHUFFLE_MS);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [n, sound]);
  return { shuffling, fakeName: roster[tick % Math.max(roster.length, 1)] };
};

const CardStage = ({ state, send }) => {
  const card = state.card;
  const { shuffling, fakeName } = useShuffle(card, state.roster || [], state.settings.sound);
  const team = teamOf(state, card?.team);
  const closed = Boolean(state.settings.closed_session);
  if (!card) {
    return (
      <div className="panel !rounded-[1.75rem] p-[2vh] flex flex-col items-center justify-center gap-[1.6vh] text-center" data-testid="boardsort-deck">
        <div className="relative h-[16vh] w-[11vh]">
          {[2, 1, 0].map((k) => <div key={k} className="absolute inset-0 rounded-2xl border-2 border-[#FACC15]/70 bg-gradient-to-br from-[#FACC15] to-[#CA8A04]" style={{ transform: `translate(${k * 6}px, ${-k * 6}px) rotate(${k * 3}deg)` }} />)}
          <Layers className="absolute inset-0 m-auto h-[5vh] w-[5vh] text-[#1A1405]" />
        </div>
        <p className="text-[2vh] text-slate-300">{state.deck_count} kart kaldı</p>
        <Button onClick={() => send?.({ type: "cmd", action: "draw_card" })} disabled={state.paused} className="h-[7vh] px-[2vw] text-[3vh]" data-testid="boardsort-draw">Kart çek</Button>
      </div>
    );
  }
  return (
    <div className="panel !rounded-[1.75rem] p-[2vh] flex flex-col items-center justify-center gap-[1.4vh] text-center" data-testid="boardsort-card">
      {!closed && (
        <p className="font-display text-[min(2.6rem,4vh)] font-bold" style={{ color: shuffling ? "#94A3B8" : team?.color || "#FACC15" }} data-testid="boardsort-card-name">
          {shuffling ? fakeName : card.name}
        </p>
      )}
      <div className={`relative w-full max-w-[36vw] min-h-[24vh] rounded-[1.5rem] grid place-items-center px-[1.2vw] py-[2vh] ${shuffling ? "anim-shake bg-gradient-to-br from-[#FACC15] to-[#CA8A04]" : "anim-flip bg-[#F8FAFC] text-[#0B0F16]"}`}
        style={{ boxShadow: "0 10px 0 rgba(0,0,0,0.35)" }}>
        {shuffling ? <Layers className="h-[6vh] w-[6vh] text-[#1A1405]" /> : (
          <>
            <span className="absolute top-[1vh] right-[1vh] rounded-full bg-[#FACC15] px-[0.8vw] py-[0.3vh] font-display text-[2.2vh] font-bold text-[#1A1405]" data-testid="boardsort-card-points">{card.points} puan</span>
            <p className="font-display text-[min(2.4rem,3.6vh)] font-bold leading-tight mt-[2vh]" data-testid="boardsort-card-text">{card.text}</p>
          </>
        )}
      </div>
      {!shuffling && (
        <div className="flex items-center gap-[1vw]">
          {card.mascot && <Mascot id={card.mascot} size="5vh" className="anim-bounce" />}
          <p className="text-[2.2vh] text-slate-200">{closed ? "Kartı doğru başlığa koyun!" : "Tahtaya gel ve kartı doğru başlığa koy!"}</p>
        </div>
      )}
    </div>
  );
};

export const BoardSortBoard = ({ state, send }) => {
  if (state.phase === "finished") return <Final state={state} />;
  const armed = Boolean(state.card) && !state.paused;
  const place = (target) => armed && send?.({ type: "cmd", action: "board_place", target });
  return (
    <div className="h-full flex gap-[1.5vw]" data-testid="board-boardsort">
      <div className="flex-1 min-w-0 flex flex-col gap-[1.4vh]">
        <div className="flex items-center gap-[1.5vw]">
          <h1 className="font-display text-[min(3rem,4.8vh)] font-bold truncate">{state.activity_title}</h1>
          <LastMove state={state} />
        </div>
        <div className="flex-1 min-h-0 grid gap-[1.2vw]" style={{ gridTemplateColumns: `repeat(${state.targets.length}, minmax(0, 1fr))` }}>
          {state.targets.map((t, i) => <Zone key={t.id} t={t} i={i} state={state} onDrop={place} armed={armed} />)}
        </div>
      </div>
      <aside className="w-[min(27vw,50vh)] shrink-0 flex flex-col gap-[1.4vh] min-h-0">
        <CardStage state={state} send={send} />
        <div className="min-h-0 overflow-hidden">{state.settings.closed_session ? <ClassScore score={state.class_total || 0} best={state.class_best} target={state.settings.class_target} /> : <div className="space-y-[1.4vh]"><ClassScore mini score={state.class_total || 0} best={state.class_best} /><Leaderboard rows={state.leaders || []} title={state.teams.length ? "Takım yarışı" : "Liderlik tablosu"} highlight={state.card?.team} /></div>}</div>
      </aside>
    </div>
  );
};

export const BoardSortStudent = ({ state, send, remaining }) => {
  if (state.phase !== "activity") return <QuizStudent state={state} send={send} remaining={remaining} />;
  return (
    <div className="space-y-5 text-center py-6" data-testid="student-boardsort">
      <p className="eyebrow">Tahtada sınıflandırma</p>
      {state.my_card ? (
        <div className="panel p-6 border-2 border-[#FACC15] anim-pulse" data-testid="boardsort-my-turn">
          <p className="font-display text-3xl font-bold text-[#FACC15]">Kart sana çıktı!</p>
          <p className="text-lg mt-2">“{state.card_text}” ({state.card_points} puan) — tahtaya gel ve doğru başlığa koy.</p>
        </div>
      ) : state.card_name ? (
        <p className="text-xl text-slate-300">Kart <b className="text-[#FACC15]">{state.card_name}</b> adlı arkadaşına çıktı. Tahtadan izle!</p>
      ) : <p className="text-xl text-slate-300">Kart çekilmesi bekleniyor…</p>}
      {state.last && <p className={`text-sm ${state.last.ok ? "text-[#4ADE80]" : "text-red-300"}`}>Son hamle: {state.last.name} · “{state.last.item}” → {state.last.target} ({state.last.ok ? "doğru" : "yanlış"})</p>}
      <p className="text-sm text-slate-400">{state.placed_count}/{state.item_count} özellik yerleşti · Puanın: {state.my_total}</p>
    </div>
  );
};

export const BoardSortTeacher = ({ state, send }) => (
  <div className="space-y-6">
    {state.leaders && (
      <div className="panel p-5 space-y-2" data-testid="teacher-boardsort-progress">
        <h3 className="font-display text-xl font-bold">Puan durumu</h3>
        {state.card && <p className="text-sm text-[#FACC15]">Açık kart: “{state.card.text}” → {state.card.name}</p>}
        {[...state.leaders].sort((a, b) => b.score - a.score).map((r, i) => <p key={r.id} className="text-sm"><b>{i + 1}.</b> <span style={{ color: r.color }}>{r.name}</span> · {r.score} puan</p>)}
      </div>
    )}
    <QuizTeacher state={state} send={send} />
  </div>
);
